import i18n from 'i18next'

import api from 'src/api'
import {
  ItripFacetedListProps,
  TripsFacetedListResponse,
  TripOriginDestination,
  ZoneName,
  ItripExportListProps,
  ItripOriginDestinationCountProps,
  TripCountInStreetSegment,
} from 'src/api/gen/api'
import { ITripListFilters } from 'src/api/types/trips'
import { ZonedDateTimeToJSON } from 'src/commons/temporalHelpers'
import { useCurrentJurisdictionKey } from 'src/layout/hooks'

import { SECONDS_IN_MINUTE } from './constants'

type QueryParams = ItripFacetedListProps | ItripExportListProps | ItripOriginDestinationCountProps

function mapFiltersToQueryParams(filters: ITripListFilters): QueryParams {
  if (!filters.layer) {
    throw Error('Layer must be defined to fetch faceted trip list !')
  }
  const queryParams: QueryParams = {
    layer: filters.layer,
    id: filters.id,
    vehicle_type: filters.vehicle_type,
    provider_ids: filters.provider,
    origins: filters.origins,
    destinations: filters.destinations,
    day_of_week: filters.dayOfWeek.map((day) => parseInt(day, 10)),
  }

  if (filters.dateStartRange.from) {
    queryParams.time_range_start_start = ZonedDateTimeToJSON(filters.dateStartRange.from)
  }
  if (filters.dateStartRange.to) {
    queryParams.time_range_start_end = ZonedDateTimeToJSON(filters.dateStartRange.to)
  }
  if (filters.dateEndRange.from) {
    queryParams.time_range_end_start = ZonedDateTimeToJSON(filters.dateEndRange.from)
  }
  if (filters.dateEndRange.to) {
    queryParams.time_range_end_end = ZonedDateTimeToJSON(filters.dateEndRange.to)
  }
  if (filters.duration) {
    queryParams.duration_min = filters.duration[0] * SECONDS_IN_MINUTE
    queryParams.duration_max = filters.duration[1] * SECONDS_IN_MINUTE
  }
  if (filters.distance) {
    queryParams.distance_min = filters.distance[0]
    queryParams.distance_max = filters.distance[1]
  }
  if (filters.tripsPerStreet) {
    queryParams.trips_per_street_min = filters.tripsPerStreet[0]
    queryParams.trips_per_street_max = filters.tripsPerStreet[1]
  }
  if (filters.location_type) {
    queryParams.location_type = filters.location_type
  }
  if (filters.speed) {
    queryParams.speed_min = filters.speed[0]
    queryParams.speed_max = filters.speed[1]
  }
  return queryParams
}

export async function fetchFacetedTripList(filters: ITripListFilters, offset = 0): Promise<TripsFacetedListResponse> {
  const queryParams = mapFiltersToQueryParams(filters)
  const response = await api.trips.tripFacetedList({ ...queryParams, offset })
  return response
}

export async function fetchOriginDestinationTripCount(
  filters: ITripListFilters,
  signal?: AbortSignal
): Promise<Array<TripOriginDestination>> {
  const queryParams = mapFiltersToQueryParams(filters)
  const response = await api.trips.tripOriginDestinationCount({ ...queryParams, options: { signal } })
  return response
}

export async function fetchExportTripList(filters: ITripListFilters): Promise<void> {
  const queryParams = mapFiltersToQueryParams(filters)
  await api.trips.tripExportList({ ...queryParams, language: i18n.language })
}

export async function fetchExportTripHeatmap(filters: ITripListFilters): Promise<void> {
  const queryParams = mapFiltersToQueryParams(filters)
  await api.trips.tripCountExportInStreetSegments({ ...queryParams, language: i18n.language })
}

export async function fetchTripCountInStreetSegment(
  filters: ITripListFilters,
  page: number,
  signal?: AbortSignal
): Promise<TripCountInStreetSegment> {
  const queryParams = mapFiltersToQueryParams(filters)
  const response = await api.trips.tripCountInStreetSegment({ ...queryParams, page, options: { signal } })
  return response
}

export function useFetchZoneNames() {
  const agencyKey = useCurrentJurisdictionKey()
  return async (layerId: string): Promise<ZoneName[]> => {
    const response = await api.jurisdiction.jurisdictionKpiGeometriesZoneNames({
      agency_key: agencyKey,
      slug: layerId,
    })
    return response
  }
}
